import { AirHandlerTypeCodeType } from '@alpinehomeair/common';
import { MAX_ZONES_PER_CONDENSER } from 'data/constants';
import {
  CondenserType,
  ConfigurationType,
  LevelType,
  PositionType,
  SpaceType,
  ZoneItemType,
  ZoneType
} from 'data/types';
import { nanoid } from 'nanoid';
import { snapAutoPlacedCondenser } from 'utils/auto-condenser-placement';
import { calculateHeightOffGround } from 'utils/configurations';
import { createHybridZoneAirHandler } from 'utils/hybrid-config-air-handlers';
import {
  collectConditionedHybridSpaces,
  groupHybridSpacesByLevel,
  HybridLevelGroup,
  HybridZonedSpace,
  hybridLevelBoundingBox,
  pickLargestHybridSpace
} from 'utils/hybrid-config-spaces';

const CONDENSER_GAP_FT = 1;
const CONDENSER_STACK_FT = 4;
const CONDENSER_TILE_FT = 3;

export const HYBRID_DEDICATED_CONFIG_NAME = 'Hybrid — Dedicated Mini-Splits';
export const HYBRID_CASSETTE_CONFIG_NAME = 'Hybrid — Cassette Mini-Splits';
export const HYBRID_COMBINED_CONFIG_NAME = 'Hybrid — Combined Mini-Splits';

export const HYBRID_AUTO_CONFIG_NAMES = [
  HYBRID_DEDICATED_CONFIG_NAME,
  HYBRID_CASSETTE_CONFIG_NAME,
  HYBRID_COMBINED_CONFIG_NAME
] as const;

type DuctlessZoning = 'dedicated' | 'combined';

function zoneItemsForSpaces(spaces: SpaceType[]): ZoneItemType[] {
  return spaces.map((space) => ({
    uniqueId: nanoid(5),
    structureItemId: space.uniqueId,
    name: space.label,
    BTUs: space.BTUs,
    originalBTUs: space.BTUs
  }));
}

function createDuctedZone(leftovers: HybridZonedSpace[]): ZoneType {
  const mount = pickLargestHybridSpace(leftovers);
  return {
    uniqueId: nanoid(5),
    name: `${mount.level.label} Ducted`,
    type: 'ducted',
    modified: false,
    zoneItems: zoneItemsForSpaces(leftovers.map((entry) => entry.space)),
    airHandler: createHybridZoneAirHandler(mount.space, 'ducted')
  };
}

function createDuctlessZone(
  entries: HybridZonedSpace[],
  name: string,
  airHandlerTypeCode: AirHandlerTypeCodeType
): ZoneType {
  const mount = pickLargestHybridSpace(entries);
  return {
    uniqueId: nanoid(5),
    name,
    type: 'ductless',
    modified: false,
    zoneItems: zoneItemsForSpaces(entries.map((entry) => entry.space)),
    airHandler: createHybridZoneAirHandler(mount.space, airHandlerTypeCode)
  };
}

function condenserPosition(
  level: LevelType,
  spaces: SpaceType[],
  stackIndex: number
): PositionType {
  const bbox = hybridLevelBoundingBox(level, spaces);
  if (!bbox) {
    return { x: 0, y: stackIndex * CONDENSER_STACK_FT };
  }
  return snapAutoPlacedCondenser(
    {
      x: bbox.maxX + CONDENSER_GAP_FT,
      y: bbox.minY + stackIndex * CONDENSER_STACK_FT
    },
    level,
    spaces,
    CONDENSER_TILE_FT,
    CONDENSER_GAP_FT
  );
}

function createLevelCondenser(
  structure: LevelType[],
  level: LevelType,
  spaces: SpaceType[],
  zones: ZoneType[],
  stackIndex: number,
  name: string
): CondenserType {
  const hasDucted = zones.some((zone) => zone.type === 'ducted');
  return {
    uniqueId: nanoid(5),
    type: hasDucted ? 'hybrid' : 'ductless',
    name,
    levelUniqueId: level.uniqueId,
    position: condenserPosition(level, spaces, stackIndex),
    placementType: 'wall',
    heightOffGround: calculateHeightOffGround(
      'wall',
      level.uniqueId,
      structure
    ),
    validSystems: [],
    validDuctedSystems: [],
    ductedAccessories: [],
    selectedAccessories: [],
    accessoriesCompleted: [],
    accessoriesNotNeeded: [],
    modified: false,
    zones
  };
}

function splitDedicatedUserRooms(
  userPlaced: HybridZonedSpace[],
  leftoverCount: number
): HybridZonedSpace[][] {
  if (userPlaced.length === 0) {
    return [];
  }
  const firstCapacity =
    leftoverCount > 0 ? MAX_ZONES_PER_CONDENSER - 1 : MAX_ZONES_PER_CONDENSER;
  const chunks: HybridZonedSpace[][] = [];
  let offset = 0;
  let capacity = firstCapacity;
  while (offset < userPlaced.length) {
    chunks.push(userPlaced.slice(offset, offset + capacity));
    offset += capacity;
    capacity = MAX_ZONES_PER_CONDENSER;
  }
  return chunks;
}

function condensersForLevel(
  structure: LevelType[],
  group: HybridLevelGroup,
  zoning: DuctlessZoning,
  ductlessAirHandlerType: AirHandlerTypeCodeType
): CondenserType[] {
  const levelSpaces = [...group.leftovers, ...group.userPlaced].map(
    (entry) => entry.space
  );
  const condenserLabel = `${group.level.label} Condenser`;

  if (zoning === 'combined') {
    const zones: ZoneType[] = [];
    if (group.leftovers.length > 0) {
      zones.push(createDuctedZone(group.leftovers));
    }
    if (group.userPlaced.length > 0) {
      zones.push(
        createDuctlessZone(
          group.userPlaced,
          `${group.level.label} Mini-Splits`,
          ductlessAirHandlerType
        )
      );
    }
    return [
      createLevelCondenser(
        structure,
        group.level,
        levelSpaces,
        zones,
        0,
        condenserLabel
      )
    ];
  }

  const userChunks = splitDedicatedUserRooms(
    group.userPlaced,
    group.leftovers.length
  );
  if (userChunks.length === 0 && group.leftovers.length > 0) {
    return [
      createLevelCondenser(
        structure,
        group.level,
        levelSpaces,
        [createDuctedZone(group.leftovers)],
        0,
        condenserLabel
      )
    ];
  }

  return userChunks.map((chunk, index) => {
    const zones: ZoneType[] = [];
    if (index === 0 && group.leftovers.length > 0) {
      zones.push(createDuctedZone(group.leftovers));
    }
    chunk.forEach((entry) => {
      zones.push(
        createDuctlessZone([entry], entry.space.label, ductlessAirHandlerType)
      );
    });
    const name =
      userChunks.length > 1 ? `${condenserLabel} ${index + 1}` : condenserLabel;
    return createLevelCondenser(
      structure,
      group.level,
      levelSpaces,
      zones,
      index,
      name
    );
  });
}

function buildHybridConfigVariant(
  structure: LevelType[],
  groups: HybridLevelGroup[],
  name: string,
  zoning: DuctlessZoning,
  ductlessAirHandlerType: AirHandlerTypeCodeType
): ConfigurationType {
  const condensers = groups.flatMap((group) =>
    condensersForLevel(structure, group, zoning, ductlessAirHandlerType)
  );
  return {
    uniqueId: nanoid(5),
    name,
    type: 'hybrid',
    condensers,
    valid: false,
    modified: false
  };
}

export function deriveHybridDuctedSpaceIds(
  structure: LevelType[],
  autoLayoutSpaceIds: readonly string[] = []
): string[] {
  return collectConditionedHybridSpaces(
    structure,
    autoLayoutSpaceIds
  ).leftovers.map((entry) => entry.space.uniqueId);
}

export function isHybridAutoGeneratedConfigName(name: string): boolean {
  return (HYBRID_AUTO_CONFIG_NAMES as readonly string[]).includes(name);
}

export function buildHybridAutoConfigurations(
  structure: LevelType[],
  autoLayoutSpaceIds: readonly string[] = []
): ConfigurationType[] {
  const { leftovers, userPlaced } = collectConditionedHybridSpaces(
    structure,
    autoLayoutSpaceIds
  );
  if (leftovers.length === 0 && userPlaced.length === 0) {
    return [];
  }

  const groups = groupHybridSpacesByLevel(structure, leftovers, userPlaced);
  const configs = [
    buildHybridConfigVariant(
      structure,
      groups,
      HYBRID_DEDICATED_CONFIG_NAME,
      'dedicated',
      'wall'
    )
  ];

  if (userPlaced.length > 0) {
    configs.push(
      buildHybridConfigVariant(
        structure,
        groups,
        HYBRID_CASSETTE_CONFIG_NAME,
        'dedicated',
        'cassette'
      )
    );
  }

  const hasCombinedLevel = groups.some((group) => group.userPlaced.length >= 2);
  if (hasCombinedLevel) {
    configs.push(
      buildHybridConfigVariant(
        structure,
        groups,
        HYBRID_COMBINED_CONFIG_NAME,
        'combined',
        'wall'
      )
    );
  }

  return configs;
}

export function buildHybridAutoConfiguration(
  structure: LevelType[],
  autoLayoutSpaceIds: readonly string[] = []
): ConfigurationType | null {
  return (
    buildHybridAutoConfigurations(structure, autoLayoutSpaceIds)[0] ?? null
  );
}
